// Deterministic SlideData validator — the code half of QA.
//
// Why this exists: the LLM "qa" stage used to be asked to do BOTH mechanical
// checks (counts, id uniqueness, cross-references, numeric ranges, enums) AND
// judgment checks (is this feedback teaching? is this tension arguable both
// ways?). The mechanical half is something code does perfectly, for free, and
// without a "high bar for failing" — so it lives here now. The qa-system.md
// prompt keeps only the judgment half.
//
// Contract source of truth: the DATA_CONTRACTS comments in agents/builder.ts
// and the critical signals in prompts/qa-system.md. Keep this in sync when a
// contract's counts/ranges change.
//
// Pure module — no React, no network, no Supabase. Safe to import anywhere.

import type { QAIssue } from './agents/types.js';
import { runCompositionChecks } from './composition/validateComposition.js';

export interface SlideValidationResult {
  ok: boolean;
  criticalIssues: QAIssue[];
  warnings: QAIssue[];
}

type Data = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Issue collector
// ---------------------------------------------------------------------------

class Ctx {
  criticalIssues: QAIssue[] = [];
  warnings: QAIssue[] = [];
  critical(location: string, issue: string, fixInstruction: string): void {
    this.criticalIssues.push({ location, issue, fixInstruction });
  }
  warn(location: string, issue: string, fixInstruction: string): void {
    this.warnings.push({ location, issue, fixInstruction });
  }
}

// ---------------------------------------------------------------------------
// Null-safe accessors
// ---------------------------------------------------------------------------

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asObj = (v: unknown): Data => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Data) : {});
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const wordCount = (s: string): number => s.trim().split(/\s+/).filter(Boolean).length;
const KEBAB = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
// Narrow on purpose: words like "todo", "tbd" or "xxxx" can legitimately
// appear in real lesson content, and this check is advisory. Only flag the
// unambiguous authoring-stub markers.
const PLACEHOLDER = /\b(lorem ipsum|placeholder text|fixme)\b/i;

// ---------------------------------------------------------------------------
// Generic checks — run for every interaction type
// ---------------------------------------------------------------------------

function genericChecks(data: Data, ctx: Ctx): void {
  // title / subtitle present and non-empty (every contract has them).
  for (const key of ['title', 'subtitle'] as const) {
    if (!asStr(data[key]).trim()) {
      ctx.critical(key, `Missing or empty "${key}".`, `Provide a non-empty "${key}".`);
    }
  }

  // Placeholder text anywhere in the tree.
  walkStrings(data, '', (val, path) => {
    if (PLACEHOLDER.test(val)) {
      ctx.critical(path, `Placeholder text detected: "${val.slice(0, 40)}".`, 'Replace with final, specific content.');
    }
  });

  // ID uniqueness (within each array) + kebab-case (every id field).
  collectObjectArrays(data, '', (path, arr) => {
    const seen = new Map<string, number>();
    arr.forEach((el, i) => {
      const id = asStr(asObj(el).id);
      if (!id) return; // not every object array carries an id
      seen.set(id, (seen.get(id) ?? 0) + 1);
      if (!KEBAB.test(id)) {
        ctx.warn(`${path}[${i}].id`, `Id "${id}" is not kebab-case.`, 'Use lowercase kebab-case ids (e.g. "factor-3").');
      }
    });
    for (const [id, count] of seen) {
      if (count > 1) {
        ctx.critical(`${path}[].id`, `Duplicate id "${id}" appears ${count}× in this array.`, 'Make every id unique within its array.');
      }
    }
  });

  // Authored close ("So what?") quality — advisory (the renderer also enforces
  // the no-dash rule at runtime; this surfaces it to the Builder so it learns).
  conclusionChecks(data, ctx);
}

// Dashes used as connectors (— em, – en). The value arrow → is fine.
const CLOSE_DASH = /[—–]/;
// Homily / filler tells — a content-blind close that restates the obvious.
const CLOSE_HOMILY: RegExp[] = [
  /that['’]s the (learning|lesson|point|takeaway)/i,
  /the key takeaway/i,
  /at the end of the day/i,
  /\bremember that\b/i,
  /the lesson here is/i,
  /what did you learn/i,
];

function conclusionChecks(data: Data, ctx: Ctx): void {
  if (!data.conclusion) return; // optional during rollout — no close yet is fine
  const c = asObj(data.conclusion);
  const texts: Array<[string, string]> = [];
  const push = (loc: string, v: unknown) => {
    const s = asStr(v);
    if (s) texts.push([loc, s]);
  };
  push('conclusion.heading', c.heading);
  push('conclusion.body', c.body);
  push('conclusion.reflectionPrompt', c.reflectionPrompt);
  const variants = Array.isArray(c.variants) ? c.variants : [];
  variants.forEach((v, i) => push(`conclusion.variants[${i}].body`, asObj(v).body));

  for (const [loc, text] of texts) {
    if (CLOSE_DASH.test(text)) {
      ctx.warn(loc, 'Close uses a dash (— or –).', 'Use commas or full stops, or rewrite. The arrow → between values is fine.');
    }
    const homily = CLOSE_HOMILY.find((re) => re.test(text));
    if (homily) {
      ctx.warn(loc, `Close uses a generic/homily phrase: "${text.match(homily)?.[0]}".`, 'Anchor the close to one concrete specific from the content; end on something concrete, not a moral.');
    }
  }

  // A catch-all variant (no `when`) is the fallback when the learner's input is
  // missing/unusable. If variants exist, one must be unconditional.
  if (variants.length > 0 && !variants.some((v) => asObj(v).when === undefined)) {
    ctx.warn('conclusion.variants', 'No catch-all variant (one with no `when`).', 'Add a variant with no `when` that reads cleanly with no {tokens}, as the fallback when the learner input is missing.');
  }
}

// ---------------------------------------------------------------------------
// Per-type deterministic rules. Keyed by interaction type id. Types without an
// entry get the generic checks only.
// ---------------------------------------------------------------------------

const PER_TYPE: Record<string, (d: Data, ctx: Ctx) => void> = {
  'reveal-predict': (d, ctx) => {
    const preds = asArr(d.predictions);
    count(ctx, 'predictions', preds.length, 1, 12);
    preds.forEach((p, i) => {
      const obj = asObj(p);
      const opts = asArr(obj.options);
      range(ctx, `predictions[${i}].options`, opts.length, 2, 4);
      const values = opts.map((o) => asStr(asObj(o).value));
      const correct = asStr(obj.correctValue);
      if (correct && !values.includes(correct)) {
        ctx.critical(`predictions[${i}].correctValue`, `correctValue "${correct}" matches no option.value.`, 'Set correctValue to one of the option values.');
      }
    });
  },

  'drag-sort': (d, ctx) => {
    const items = asArr(d.items);
    const zones = asArr(d.zones);
    range(ctx, 'items', items.length, 4, 8);
    range(ctx, 'zones', zones.length, 2, 4);
    const zoneIds = new Set(zones.map((z) => asStr(asObj(z).id)));
    items.forEach((it, i) => {
      const cz = asStr(asObj(it).correctZone);
      if (cz && !zoneIds.has(cz)) {
        ctx.critical(`items[${i}].correctZone`, `correctZone "${cz}" references no zone id.`, 'Point correctZone at an existing zone id.');
      }
    });
  },

  'teach-back': (d, ctx) => {
    const VALID_KINDS = new Set(['perspective', 'targeted', 'ownership', 'backfire', 'answer']);
    // Diagnosis: misconceptionId must resolve to an option, and a correct-flagged
    // option must exist.
    const options = asArr(d.diagnosisOptions);
    const optionIds = new Set(options.map((o) => asStr(asObj(o).id)));
    const misconceptionId = asStr(d.misconceptionId);
    if (misconceptionId && !optionIds.has(misconceptionId)) {
      ctx.critical('misconceptionId', `misconceptionId "${misconceptionId}" matches no diagnosisOption id.`, 'Point misconceptionId at an existing diagnosisOption id.');
    }
    if (!options.some((o) => asObj(o).correct === true)) {
      ctx.critical('diagnosisOptions', 'No diagnosisOption is flagged correct.', 'Mark exactly one diagnosisOption as correct (the authored misconception).');
    }
    const distractors = options.filter((o) => asStr(asObj(o).id) !== misconceptionId).length;
    if (distractors < 2) {
      ctx.warn('diagnosisOptions', `Only ${distractors} diagnosis distractor(s).`, 'Add plausible distractor diagnoses (≥2) so the diagnosis tests understanding.');
    }
    // Artifact: needs ≥1 damage fragment AND ≥1 load-bearing fragment.
    const artifactFragments = asArr(asObj(d.artifact).fragments);
    if (!artifactFragments.some((f) => asObj(f).damage === true)) {
      ctx.critical('artifact.fragments', 'Artifact has no damage fragment.', 'Mark at least one strikeable fragment as damage:true — the phrase the learner should strike.');
    }
    if (!artifactFragments.some((f) => asObj(f).loadBearing === true)) {
      ctx.critical('artifact.fragments', 'Artifact has no load-bearing fragment.', 'Add one loadBearing:true fragment a careless reviewer would wrongly strike, so over-striking is punishable.');
    }
    // Coaching fragments: need a backfire and an answer fragment for the wrong-move branches.
    const coaching = asArr(d.coachingFragments);
    const kinds = new Set(coaching.map((f) => asStr(asObj(f).kind)));
    if (!kinds.has('backfire') && !kinds.has('answer')) {
      ctx.warn('coachingFragments', 'No backfire or answer-giving coaching fragment.', 'Include at least one backfire and one answer-giving fragment so the wrong-move branches are reachable.');
    }
    // Revision: exactly one residue fragment.
    const revisionFragments = asArr(asObj(d.revision).fragments);
    const residueCount = revisionFragments.filter((f) => asObj(f).isResidue === true).length;
    if (residueCount !== 1) {
      ctx.critical('revision.fragments', `Revision has ${residueCount} residue fragments (need exactly 1).`, 'Mark exactly one revision fragment isResidue:true — the belief’s fingerprint surviving the fix.');
    }
    // Branches: `when` predicates must reference known coach kinds.
    asArr(d.branches).forEach((b, i) => {
      const when = asObj(asObj(b).when);
      const opens = asStr(when.opensWithKind);
      if (opens && !VALID_KINDS.has(opens)) {
        ctx.critical(`branches[${i}].when.opensWithKind`, `Unknown coach kind "${opens}".`, 'Use a coach kind: perspective, targeted, ownership, backfire, or answer.');
      }
      for (const key of ['includesAnyKind', 'excludesAllKinds'] as const) {
        asArr(when[key]).forEach((k, j) => {
          const kind = asStr(k);
          if (kind && !VALID_KINDS.has(kind)) {
            ctx.critical(`branches[${i}].when.${key}[${j}]`, `Unknown coach kind "${kind}".`, 'Use a coach kind: perspective, targeted, ownership, backfire, or answer.');
          }
        });
      }
    });
  },

  'tension-mapper': (d, ctx) => {
    const factors = asArr(d.factors);
    range(ctx, 'factors', factors.length, 6, 10);
    const factorIds = new Set<string>();
    factors.forEach((f, i) => {
      const obj = asObj(f);
      factorIds.add(asStr(obj.id));
      if (asStr(obj.description).length > 80) {
        ctx.warn(`factors[${i}].description`, 'Factor description exceeds 80 chars.', 'Trim to ≤80 chars.');
      }
    });
    const insights = asArr(asObj(d.expertAnalysis).insights);
    insights.forEach((ins, i) => {
      const fid = asStr(asObj(ins).factorId);
      if (fid && !factorIds.has(fid)) {
        ctx.critical(`expertAnalysis.insights[${i}].factorId`, `factorId "${fid}" references no factor.`, 'Reference an existing factor id.');
      }
    });
  },

  'scenario-simulation': (d, ctx) => {
    const branches = asArr(d.branches);
    const first = asObj(branches[0]);
    if (asStr(first.id) !== 'start' || asStr(first.type) !== 'decision') {
      ctx.critical('branches[0]', 'First branch must have id "start" and type "decision".', 'Make the first branch {id:"start", type:"decision"}.');
    }
    const ids = new Set(branches.map((b) => asStr(asObj(b).id)));
    const decisions = branches.filter((b) => asStr(asObj(b).type) === 'decision').length;
    const endings = branches.filter((b) => asStr(asObj(b).type) === 'ending').length;
    range(ctx, 'branches(decision)', decisions, 3, 5);
    range(ctx, 'branches(ending)', endings, 2, 3);
    branches.forEach((b, i) => {
      asArr(asObj(b).choices).forEach((c, j) => {
        const nb = asStr(asObj(c).nextBranch);
        if (nb && !ids.has(nb)) {
          ctx.critical(`branches[${i}].choices[${j}].nextBranch`, `nextBranch "${nb}" references no branch.`, 'Point nextBranch at an existing branch id.');
        }
      });
    });
  },

  'before-after-bridge': (d, ctx) => {
    const before = asArr(asObj(d.beforePhase).prompts);
    const after = asArr(asObj(d.afterPhase).prompts);
    const beforeIds = new Set(before.map((p) => asStr(asObj(p).id)));
    const afterIds = new Set(after.map((p) => asStr(asObj(p).id)));
    range(ctx, 'beforePhase.prompts', before.length, 2, 4);
    after.forEach((p, i) => {
      const id = asStr(asObj(p).id);
      if (id && !beforeIds.has(id)) {
        ctx.critical(`afterPhase.prompts[${i}].id`, `after-phase id "${id}" has no matching before-phase prompt.`, 'Every after-phase prompt id must match a before-phase id.');
      }
    });
    for (const id of beforeIds) {
      if (id && !afterIds.has(id)) {
        ctx.critical('afterPhase.prompts', `before-phase id "${id}" has no matching after-phase prompt.`, 'Every before-phase prompt needs an after-phase counterpart with the same id.');
      }
    }
  },

  'claim-evidence': (d, ctx) => {
    const pool = asArr(d.evidencePool);
    range(ctx, 'evidencePool', pool.length, 6, 10);
    const ids = new Set(pool.map((e) => asStr(asObj(e).id)));
    let distractors = 0;
    pool.forEach((e, i) => {
      const strength = asStr(asObj(e).strength);
      if (!['strong', 'partial', 'distractor'].includes(strength)) {
        ctx.critical(`evidencePool[${i}].strength`, `Invalid strength "${strength}".`, 'Use "strong" | "partial" | "distractor".');
      }
      if (strength === 'distractor') distractors += 1;
    });
    if (distractors < 2) {
      ctx.critical('evidencePool', `Only ${distractors} distractor(s); need at least 2.`, 'Add distractor evidence so the selection is a real discrimination task.');
    }
    asArr(asObj(d.expertModel).selectedEvidence).forEach((ev, i) => {
      const id = asStr(ev);
      if (id && !ids.has(id)) {
        ctx.critical(`expertModel.selectedEvidence[${i}]`, `selectedEvidence "${id}" references no evidence item.`, 'Reference existing evidence ids.');
      }
    });
  },

  'hotspot-explorer': (d, ctx) => {
    const spots = asArr(d.hotspots);
    range(ctx, 'hotspots', spots.length, 3, 8);
    if (!asStr(d.imageUrl).trim()) ctx.critical('imageUrl', 'Missing imageUrl.', 'Provide a hosted image URL.');
    if (!asStr(d.imageAlt).trim()) ctx.critical('imageAlt', 'Missing imageAlt (a11y).', 'Describe the image for screen-reader parity.');
    spots.forEach((s, i) => {
      const obj = asObj(s);
      for (const axis of ['x', 'y'] as const) {
        const v = obj[axis];
        if (!isNum(v) || v < 0 || v > 100) {
          ctx.critical(`hotspots[${i}].${axis}`, `${axis}=${String(v)} is outside 0–100.`, `Set ${axis} to a 0–100 percentage.`);
        }
      }
    });
  },

  'reflect-act': (d, ctx) => {
    const stages = asArr(d.stages);
    const ids = stages.map((s) => asStr(asObj(s).id));
    const expected = ['what', 'so-what', 'now-what'];
    if (stages.length !== 3 || expected.some((e, i) => ids[i] !== e)) {
      ctx.critical('stages', `Stages must be exactly [what, so-what, now-what]; got [${ids.join(', ')}].`, 'Use the fixed 3-stage tuple with ids what / so-what / now-what.');
    }
    stages.forEach((s, i) => range(ctx, `stages[${i}].prompts`, asArr(asObj(s).prompts).length, 1, 3));
  },

  'scaffolded-investigation': (d, ctx) => {
    const rounds = asArr(d.rounds);
    if (rounds.length !== 3) {
      ctx.critical('rounds', `Expected exactly 3 rounds; got ${rounds.length}.`, 'Provide 3 rounds: guided → semi-guided → independent.');
    }
    rounds.forEach((r, i) => {
      const obj = asObj(r);
      if (isNum(obj.roundNumber) && obj.roundNumber !== i + 1) {
        ctx.critical(`rounds[${i}].roundNumber`, `roundNumber ${obj.roundNumber} is out of order.`, `Set roundNumber to ${i + 1}.`);
      }
    });
  },

  'timeline-navigator': (d, ctx) => {
    const events = asArr(d.events);
    range(ctx, 'events', events.length, 5, 10);
    const milestones = events.filter((e) => asObj(e).isMilestone === true).length;
    if (milestones < 2) {
      ctx.warn('events', `Only ${milestones} milestone(s); at least 2 recommended.`, 'Mark at least 2 events as milestones to anchor the arc.');
    }
  },

  'progressive-builder': (d, ctx) => {
    range(ctx, 'layers', asArr(d.layers).length, 3, 5);
  },

  'layered-reveal': (d, ctx) => {
    const layers = asArr(d.layers);
    range(ctx, 'layers', layers.length, 3, 5);
    layers.forEach((l, i) => {
      if (wordCount(asStr(asObj(l).content)) > 150) {
        ctx.warn(`layers[${i}].content`, 'Layer content exceeds 150 words.', 'Trim to ≤150 words per layer.');
      }
    });
  },

  'guided-sandbox': (d, ctx) => {
    const dims = asArr(d.dimensions);
    range(ctx, 'dimensions', dims.length, 3, 5);
    dims.forEach((dim, i) => {
      if (asArr(asObj(dim).keywords).length === 0) {
        ctx.critical(`dimensions[${i}].keywords`, 'Dimension has no scoring keywords.', 'Add concrete keyword phrases — they are the scoring signal.');
      }
    });
    const min = d.minScore;
    if (isNum(min) && (min < 0 || min > 100)) {
      ctx.critical('minScore', `minScore ${min} is outside 0–100.`, 'Set minScore between 0 and 100.');
    }
  },

  'confidence-meter': (d, ctx) => {
    const items = asArr(d.items);
    range(ctx, 'items', items.length, 4, 6);
    items.forEach((it, i) => {
      const labels = asArr(asObj(asObj(it).scale).labels);
      if (labels.length !== 3) {
        ctx.warn(`items[${i}].scale.labels`, `Expected 3 scale labels; got ${labels.length}.`, 'Provide [low, mid, high] labels.');
      }
    });
  },

  'perspective-lenses': (d, ctx) => {
    const lenses = asArr(d.lenses);
    range(ctx, 'lenses', lenses.length, 3, 5);
    lenses.forEach((l, i) => range(ctx, `lenses[${i}].subPrompts`, asArr(asObj(l).subPrompts).length, 2, 4));
    const ctxWords = wordCount(asStr(asObj(d.stimulus).context));
    if (ctxWords && (ctxWords < 80 || ctxWords > 150)) {
      ctx.warn('stimulus.context', `Stimulus is ${ctxWords} words (target 80–150).`, 'Aim for an 80–150 word stimulus.');
    }
  },

  'gallery-walk': (d, ctx) => {
    range(ctx, 'stations', asArr(d.stations).length, 4, 6);
  },

  'spot-the-error': (d, ctx) => {
    const sections = asArr(d.sections);
    range(ctx, 'sections', sections.length, 2, 4);
    sections.forEach((s, i) => {
      const errors = asArr(asObj(s).content).filter((c) => asStr(asObj(c).type) === 'error').length;
      if (errors < 2) {
        ctx.warn(`sections[${i}].content`, `Section has ${errors} error chunk(s); 2–4 recommended.`, 'Embed 2–4 error chunks per section.');
      }
    });
  },

  'source-examination': (d, ctx) => {
    const src = asObj(d.source);
    if (!asStr(src.body).trim() && !asStr(src.imageUrl).trim()) {
      ctx.critical('source', 'Source has neither body nor imageUrl.', 'Provide a text body or an image for the source.');
    }
    if (asStr(src.imageUrl).trim() && !asStr(src.imageAlt).trim()) {
      ctx.critical('source.imageAlt', 'Image source missing imageAlt (a11y).', 'Describe the image for screen-reader parity.');
    }
    const questions = asArr(d.questions);
    range(ctx, 'questions', questions.length, 3, 12);
    const SKILLS = ['extraction', 'concept', 'interpretation', 'analysis', 'reliability', 'comparison', 'synthesis'];
    const LEVELS = ['L1_recall', 'L2_interpret', 'L3_apply'];
    const levelsSeen = new Set<string>();
    questions.forEach((q, i) => {
      const obj = asObj(q);
      const opts = asArr(obj.options);
      if (opts.length < 2) {
        ctx.critical(`questions[${i}].options`, `Question has ${opts.length} option(s); need at least 2.`, 'Provide at least 2 options with real distractor pressure.');
      }
      if (!SKILLS.includes(asStr(obj.skill))) {
        ctx.critical(`questions[${i}].skill`, `Invalid skill "${asStr(obj.skill)}".`, `Use one of: ${SKILLS.join(', ')}.`);
      }
      const level = asStr(obj.cognitiveLevel);
      if (!LEVELS.includes(level)) {
        ctx.critical(`questions[${i}].cognitiveLevel`, `Invalid cognitiveLevel "${level}".`, 'Use L1_recall | L2_interpret | L3_apply.');
      } else {
        levelsSeen.add(level);
      }
      if (!asStr(obj.feedback).trim()) {
        ctx.critical(`questions[${i}].feedback`, 'Question missing feedback.', 'Add feedback explaining WHY the answer is right — the load-bearing learning.');
      }
      const correct = obj.correct;
      const indices = Array.isArray(correct) ? correct : [correct];
      indices.forEach((c) => {
        if (!isNum(c) || c < 0 || c >= opts.length) {
          ctx.critical(`questions[${i}].correct`, `correct index ${String(c)} is out of range.`, `Reference an option index between 0 and ${opts.length - 1}.`);
        }
      });
    });
    if (levelsSeen.size < 2) {
      ctx.warn('questions', `Questions span ${levelsSeen.size} cognitive level(s); the L1→L3 ladder wants ≥2.`, 'Spread questions across at least 2 cognitive levels.');
    }
  },

  'revision-exam': (d, ctx) => {
    const sources = asArr(d.sources);
    const questions = asArr(d.questions);
    if (sources.length === 0) ctx.critical('sources', 'No sources.', 'Provide at least one source document.');
    range(ctx, 'questions', questions.length, 3, 30);
    const sourceKeys = new Set(sources.map((s) => asStr(asObj(s).key)));
    const SKILLS = ['extraction', 'concept', 'interpretation', 'analysis', 'reliability', 'comparison', 'synthesis'];
    const LEVELS = ['L1_recall', 'L2_interpret', 'L3_apply'] as const;
    const levelsSeen = new Set<string>();
    // CAPS mark weighting — accumulate marks per cognitive level (questions + paragraph steps).
    const marksByLevel: Record<string, number> = { L1_recall: 0, L2_interpret: 0, L3_apply: 0 };
    let totalMarks = 0;

    // Visual-source warning (#10): a visual source type with no image can't be shown.
    const VISUAL = /cartoon|photo|image|map|poster|picture|painting|drawing|graph/i;
    sources.forEach((s, i) => {
      const obj = asObj(s);
      if (VISUAL.test(asStr(obj.type)) && !asStr(obj.imageUrl).trim() && !asStr(obj.body).trim()) {
        ctx.warn(`sources[${i}]`, `Source "${asStr(obj.label) || asStr(obj.type)}" looks visual but has no image or descriptive body.`, 'Add a hosted imageUrl, or describe the visual in body so it can be shown.');
      }
    });

    questions.forEach((q, i) => {
      const obj = asObj(q);
      if (!sourceKeys.has(asStr(obj.sourceKey))) {
        ctx.critical(`questions[${i}].sourceKey`, `sourceKey "${asStr(obj.sourceKey)}" references no source.`, 'Reference an existing source key.');
      }
      const opts = asArr(obj.options).map(asStr);
      if (opts.length < 2) ctx.critical(`questions[${i}].options`, 'Fewer than 2 options.', 'Provide at least 2 options.');
      if (!SKILLS.includes(asStr(obj.skill))) ctx.critical(`questions[${i}].skill`, `Invalid skill "${asStr(obj.skill)}".`, `Use one of: ${SKILLS.join(', ')}.`);
      const level = asStr(obj.cognitiveLevel);
      if (!LEVELS.includes(level as (typeof LEVELS)[number])) ctx.critical(`questions[${i}].cognitiveLevel`, `Invalid cognitiveLevel "${level}".`, 'Use L1_recall | L2_interpret | L3_apply.');
      else levelsSeen.add(level);
      if (!asStr(obj.feedback).trim()) ctx.critical(`questions[${i}].feedback`, 'Missing feedback.', 'Add feedback explaining WHY.');
      const correctArr = Array.isArray(obj.correct) ? obj.correct : [obj.correct];
      correctArr.forEach((c) => {
        if (!isNum(c) || c < 0 || c >= opts.length) ctx.critical(`questions[${i}].correct`, `correct index ${String(c)} out of range.`, `Use 0–${opts.length - 1}.`);
      });
      // Mark accounting.
      const m = isNum(obj.marks) ? (obj.marks as number) : 0;
      totalMarks += m;
      if (level in marksByLevel) marksByLevel[level] += m;
      // Distractor length parity (#5) — single-select MCQs only.
      checkDistractorParity(ctx, `questions[${i}]`, opts, correctArr);
    });
    if (levelsSeen.size < 2) ctx.warn('questions', `Questions span ${levelsSeen.size} cognitive level(s); the L1→L3 ladder wants ≥2.`, 'Spread questions across at least 2 cognitive levels.');

    // Paragraph builder (#7) — four pick-from-choices synthesis steps (all L3).
    const pb = asObj(d.paragraphBuilder);
    const pbSteps = asArr(pb.steps);
    if (Object.keys(pb).length > 0) {
      if (!asStr(pb.task).trim()) ctx.warn('paragraphBuilder.task', 'Paragraph builder has no task.', 'State the paragraph question the steps build toward.');
      if (pbSteps.length !== 4) ctx.warn('paragraphBuilder.steps', `Paragraph builder has ${pbSteps.length} steps (expected 4: topic → evidence → knowledge → conclusion).`, 'Provide exactly 4 steps.');
      pbSteps.forEach((s, i) => {
        const obj = asObj(s);
        const opts = asArr(obj.options).map(asStr);
        if (opts.length < 2) ctx.critical(`paragraphBuilder.steps[${i}].options`, 'Fewer than 2 options.', 'Provide at least 2 options.');
        if (!isNum(obj.correct) || (obj.correct as number) < 0 || (obj.correct as number) >= opts.length) {
          ctx.critical(`paragraphBuilder.steps[${i}].correct`, `correct index ${String(obj.correct)} out of range.`, `Use 0–${opts.length - 1}.`);
        }
        if (!asStr(obj.feedback).trim()) ctx.warn(`paragraphBuilder.steps[${i}].feedback`, 'Step has no feedback.', 'Explain why the choice is right.');
        const m = isNum(obj.marks) ? (obj.marks as number) : 0;
        totalMarks += m;
        marksByLevel.L3_apply += m; // the synthesis paragraph is L3
      });
    }

    // Mark-balance gate (#3 / #9) — the ONE hard-stop. CAPS source-based question
    // is weighted 15 / 20 / 15 = 50. Off by >3 on a level, or a wrong total,
    // means the paper was mis-tagged (or isn't a full source-based question).
    if (totalMarks > 0) {
      const TARGET: Record<string, number> = { L1_recall: 15, L2_interpret: 20, L3_apply: 15 };
      const EXAM_TOTAL = 50;
      if (totalMarks !== EXAM_TOTAL) {
        ctx.critical('markBalance', `Marks total ${totalMarks}, expected ${EXAM_TOTAL} for a source-based question.`, 'A CAPS source-based question is 50 marks. Re-check the paper — every sub-question (and the paragraph) must be tagged with its marks.');
      }
      (Object.keys(TARGET)).forEach((lvl) => {
        const diff = Math.abs(marksByLevel[lvl] - TARGET[lvl]);
        if (diff > 3) {
          ctx.critical('markBalance', `L${lvl[1]} marks total ${marksByLevel[lvl]}, expected ~${TARGET[lvl]} (off by ${diff}).`, 'Re-check the memo: cognitive levels must sum near 15 / 20 / 15 (±3).');
        } else if (diff >= 1) {
          ctx.warn('markBalance', `L${lvl[1]} marks total ${marksByLevel[lvl]}, target ~${TARGET[lvl]} (off by ${diff}).`, 'Normal DBE drift up to ±1; verify against the memo.');
        }
      });
    }
  },

  'mark-this-answer': (d, ctx) => {
    if (!asStr(d.task).trim()) {
      ctx.critical('task', 'Missing task (the question the answers respond to).', 'State the question being evaluated.');
    }
    const answers = asArr(d.answers);
    range(ctx, 'answers', answers.length, 1, 3);
    const VERDICTS = ['yes', 'partial', 'no'];
    answers.forEach((a, i) => {
      const obj = asObj(a);
      if (!asStr(obj.text).trim()) {
        ctx.critical(`answers[${i}].text`, 'Sample answer has no text.', 'Provide the answer the learner evaluates.');
      }
      const criteria = asArr(obj.criteria);
      if (criteria.length === 0) {
        ctx.critical(`answers[${i}].criteria`, 'Answer has no rubric criteria.', 'Add at least one criterion to evaluate.');
      }
      criteria.forEach((c, j) => {
        const co = asObj(c);
        if (!VERDICTS.includes(asStr(co.correct))) {
          ctx.critical(`answers[${i}].criteria[${j}].correct`, `Invalid verdict "${asStr(co.correct)}".`, 'Use yes | partial | no.');
        }
        if (!asStr(co.explanation).trim()) {
          ctx.warn(`answers[${i}].criteria[${j}].explanation`, 'Criterion has no explanation.', 'Explain the examiner judgement — it is the learning.');
        }
      });
    });
  },

  // Composed experiences (Kula Create Phase 1) run the composition grammar
  // (§3.6) instead of a single fixed-type ruleset. Generic checks still ran
  // above (title/subtitle, placeholders, id uniqueness, conclusion prose).
  'composed-experience': (d, ctx) => runCompositionChecks(d, ctx),
};

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export function validateSlideData(
  data: unknown,
  interactionType: string,
): SlideValidationResult {
  const ctx = new Ctx();
  const obj = asObj(data);
  genericChecks(obj, ctx);
  PER_TYPE[interactionType]?.(obj, ctx);
  return {
    ok: ctx.criticalIssues.length === 0,
    criticalIssues: ctx.criticalIssues,
    warnings: ctx.warnings,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function count(ctx: Ctx, location: string, n: number, min: number, max: number): void {
  range(ctx, location, n, min, max);
}

// Item-count bounds are a QUALITY nudge, not a breakage — real lean content
// can legitimately sit below the ideal range (e.g. a 1-section spot-the-error
// smoke slide). So these are WARNINGS. The things that actually break an
// experience — dangling id references, out-of-range coordinates, invalid
// enums, wrong fixed-tuple structure, missing required fields — are pushed as
// CRITICAL inline at their call sites.
function range(ctx: Ctx, location: string, n: number, min: number, max: number): void {
  if (n < min || n > max) {
    ctx.warn(location, `Expected ${min}–${max} items; got ${n}.`, `Aim for between ${min} and ${max} items.`);
  }
}

/**
 * Distractor length-parity check (REVISION_TEMPLATE_PLAN.md §11 #5). Advisory
 * only. Ported from the colleague's CAPS skill: every wrong option should be
 * within ~0.7–1.43× the correct option's word count, so "the longest answer is
 * usually right" can't be gamed. Single-select MCQs only; skips very short
 * options (e.g. one-word multi-select like city names).
 */
function checkDistractorParity(ctx: Ctx, location: string, opts: string[], correct: unknown[]): void {
  if (opts.length < 2 || correct.length !== 1) return;
  const ci = correct[0];
  if (typeof ci !== 'number' || ci < 0 || ci >= opts.length) return;
  const refWc = opts[ci].trim().split(/\s+/).filter(Boolean).length;
  if (refWc <= 2) return; // too short for parity to mean anything
  opts.forEach((opt, i) => {
    if (i === ci) return;
    const wc = opt.trim().split(/\s+/).filter(Boolean).length;
    if (wc === 0) return;
    const ratio = wc / refWc;
    if (ratio < 0.7 || ratio > 1.43) {
      ctx.warn(`${location}.options[${i}]`, `Option is ${wc} words vs the correct answer's ${refWc} (ratio ${ratio.toFixed(2)}).`, 'Keep every option within ~0.7–1.4× the correct answer so length never gives it away.');
    }
  });
}

function walkStrings(node: unknown, path: string, cb: (val: string, path: string) => void): void {
  if (typeof node === 'string') {
    if (node) cb(node, path || '(root)');
  } else if (Array.isArray(node)) {
    node.forEach((el, i) => walkStrings(el, `${path}[${i}]`, cb));
  } else if (node && typeof node === 'object') {
    for (const k of Object.keys(node as Data)) {
      walkStrings((node as Data)[k], path ? `${path}.${k}` : k, cb);
    }
  }
}

function collectObjectArrays(
  node: unknown,
  path: string,
  cb: (path: string, arr: Data[]) => void,
): void {
  if (Array.isArray(node)) {
    const objs = node.filter((x) => x && typeof x === 'object' && !Array.isArray(x)) as Data[];
    if (objs.length === node.length && node.length > 0) cb(path, objs);
    node.forEach((el, i) => collectObjectArrays(el, `${path}[${i}]`, cb));
  } else if (node && typeof node === 'object') {
    for (const k of Object.keys(node as Data)) {
      collectObjectArrays((node as Data)[k], path ? `${path}.${k}` : k, cb);
    }
  }
}
